
import { useState, useEffect } from "react";
import logolight from "../../assets/Netraa Logo Transparent.png";
import logodark from "../../assets/Netraa Logo Dark.png";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { FiSun, FiMoon } from "react-icons/fi";
import { MdVerified, MdLockPerson, MdVerifiedUser, MdArrowForward } from "react-icons/md";
import logofooter from "../../assets/Apmosys Products Transparent logo.png"

import {
  CpuChipIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CircleStackIcon,
  Squares2X2Icon,
  LockClosedIcon,
  ServerStackIcon,
  ShieldExclamationIcon,
  ComputerDesktopIcon,
  LinkSlashIcon
} from "@heroicons/react/24/outline";

const useCases = [
  {
    title: "Thread Pool Saturation",
    desc: "System throughput drops as worker threads hit maximum capacity, causing request queues to grow and responses to delay.",
    icon: <CpuChipIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "1.1",
        title: "Problem",
        desc: "Worker threads are fully utilized, leading to increased queue length and delayed request processing."
      },
      {
        num: "1.2",
        title: "Netraa Insight",
        desc: "Thread pools are saturated under load, confirming requests are waiting instead of being processed."
      },
      {
        num: "1.3",
        title: "Solution",
        desc: "Increase thread pool limits and optimize request handling to ensure faster thread release."
      }
    ]
  },
  {
    title: "Downstream Dependency Bottleneck",
    desc: "High latency in Debit Validate OTP API causes threads to remain occupied and reduces system capacity.",
    icon: <ClockIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "2.1",
        title: "Problem",
        desc: "OTP validation calls take 18–19 seconds, blocking available worker threads."
      },
      {
        num: "2.2",
        title: "Netraa Insight",
        desc: "Distributed tracing identified a JSP downstream call as the primary latency contributor."
      },
      {
        num: "2.3",
        title: "Solution",
        desc: "Optimize JSP logic and introduce timeouts and circuit breakers for resilience."
      }
    ]
  },
  {
    title: "Null Pointer Exception (smsbanking)",
    desc: "ReKycSubmission endpoint fails due to null reference after database query execution.",
    icon: <ExclamationTriangleIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "3.1",
        title: "Problem",
        desc: "NullPointerException occurs at line 591 in ReKycService after DB response."
      },
      {
        num: "3.2",
        title: "Netraa Insight",
        desc: "Oracle query returns missing data leading to unhandled null access."
      },
      {
        num: "3.3",
        title: "Solution",
        desc: "Add null checks and validate DB response integrity before object access."
      }
    ]
  },
  {
    title: "Database Connection & Logic Errors",
    desc: "Service failures due to incorrect DB configuration and missing stored procedures.",
    icon: <CircleStackIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "4.1",
        title: "Problem",
        desc: "Connection failures on outdated DB port and missing stored procedures."
      },
      {
        num: "4.2",
        title: "Netraa Insight",
        desc: "Trace shows port 1433 mismatch and missing dbo.SP_* procedure errors."
      },
      {
        num: "4.3",
        title: "Solution",
        desc: "Update DB configuration and deploy missing stored procedures."
      }
    ]
  },
  {
    title: "Application Architecture Inefficiency",
    desc: "Underwriting flows are slowed by synchronous calls and invalid backend dependencies.",
    icon: <Squares2X2Icon className="w-5 h-5" />,
    subItems: [
      {
        num: "5.1",
        title: "Problem",
        desc: "Heavy synchronous workflows and calls to non-existent services increase latency."
      },
      {
        num: "5.2",
        title: "Netraa Insight",
        desc: "Repeated invalid endpoint calls create cascading delays across services."
      },
      {
        num: "5.3",
        title: "Solution",
        desc: "Shift to asynchronous, event-driven architecture and remove invalid API calls."
      }
    ]
  },
  {
    title: "Database Contention & Locking",
    desc: "SQL performance issues caused by long-running procedures and locking during peak load.",
    icon: <LockClosedIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "6.1",
        title: "Problem",
        desc: "High contention and locking in accounting and posting procedures."
      },
      {
        num: "6.2",
        title: "Netraa Insight",
        desc: "DB traces show cursor usage and blocking queries during peak operations."
      },
      {
        num: "6.3",
        title: "Solution",
        desc: "Optimize SQL procedures and introduce batching/throttling strategies."
      }
    ]
  },
  {
    title: "Infrastructure Storage Failure",
    desc: "Batch processing fails due to full disk storage on the server.",
    icon: <ServerStackIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "7.1",
        title: "Problem",
        desc: "Health batch server stops due to 100% disk utilization on /home partition."
      },
      {
        num: "7.2",
        title: "Netraa Insight",
        desc: "Monitoring flags critical storage exhaustion causing service outage."
      },
      {
        num: "7.3",
        title: "Solution",
        desc: "Expand storage and enable proactive threshold alerts at 80% usage."
      }
    ]
  },
  {
    title: "Database Permission Denied",
    desc: "Service failures due to insufficient database privileges for update operations.",
    icon: <ShieldExclamationIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "8.1",
        title: "Problem",
        desc: "ExecuteUpdate API fails due to restricted DB user permissions."
      },
      {
        num: "8.2",
        title: "Netraa Insight",
        desc: "Privilege checks confirm missing authorization for required operations."
      },
      {
        num: "8.3",
        title: "Solution",
        desc: "Grant required DB permissions to application service user."
      }
    ]
  },
  {
    title: "UI Thread Deadlock",
    desc: "UI freezes caused by improper thread synchronization in dialog handling.",
    icon: <ComputerDesktopIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "9.1",
        title: "Problem",
        desc: "Background threads incorrectly trigger UI dialogs causing deadlocks."
      },
      {
        num: "9.2",
        title: "Netraa Insight",
        desc: "Thread synchronization breakdown identified in UI event handling."
      },
      {
        num: "9.3",
        title: "Solution",
        desc: "Ensure UI updates run on main thread with proper synchronization."
      }
    ]
  },
  {
    title: "Connection Pool Leak",
    desc: "Gradual slowdown and failure due to unreleased database connections.",
    icon: <LinkSlashIcon className="w-5 h-5" />,
    subItems: [
      {
        num: "10.1",
        title: "Problem",
        desc: "Connections are borrowed but not returned to the pool."
      },
      {
        num: "10.2",
        title: "Netraa Insight",
        desc: "Pool saturation detected even under low traffic conditions."
      },
      {
        num: "10.3",
        title: "Solution",
        desc: "Fix resource cleanup logic and enforce strict connection handling policies."
      }
    ]
  }
];

export default function UseCases() {
  const [darkMode, setDarkMode] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // index of open use case
  const navigate = useNavigate();

  useEffect(() => {
    const theme = localStorage.getItem("theme") || "light";
    setDarkMode(theme === "dark");
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = activeModal !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeModal]);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const openModal = (i) => setActiveModal(i);
  const closeModal = () => setActiveModal(null);

  const modalData = activeModal !== null ? useCases[activeModal] : null;

  return (
    <div className="bg-white dark:bg-[#141414] text-gray-900 dark:text-gray-100 transition-colors duration-300">

      {/* ── HEADER ── */}
      <header className="header bg-white dark:bg-[#141414] border-b border-gray-200 dark:border-gray-800 px-2 lg:px-6">
        <div className="flex items-center justify-between h-full">

          {/* LEFT: LOGO */}
          <div className="flex items-center gap-3 w-64">
            <div className="flex items-center justify-start -ml-4">
              <img src={logolight} alt="Netraa Docs Light" className="h-24 mt-3 w-auto dark:hidden" />
              <img src={logodark}  alt="Netraa Docs Dark"  className="h-24 mt-3 w-auto hidden dark:block" />
            </div>
          </div>

          {/* RIGHT: THEME + BACK */}
          <div className="flex items-center gap-6 w-64 justify-end">
            <button
              onClick={toggleTheme}
              aria-label="Toggle Dark Mode"
              className="relative flex items-center w-8 h-6 sm:w-12 sm:h-7
                         rounded-full bg-gray-300 dark:bg-orange-500
                         transition px-1 overflow-hidden"
            >
              <span className="w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full shadow-md
                               transform transition-transform duration-300
                               flex items-center justify-center
                               translate-x-0 dark:translate-x-2 sm:dark:translate-x-5">
                <FiSun  className="text-orange-500 text-[10px] sm:text-xs dark:hidden" />
                <FiMoon className="text-gray-700  text-[10px] sm:text-xs hidden dark:block" />
              </span>
            </button>

            <div className="relative group">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1
                               px-2 py-1 text-xs rounded bg-gray-800 text-white
                               opacity-0 group-hover:opacity-100 transition
                               pointer-events-none whitespace-nowrap">
                Back
              </span>
            </div>
          </div>
        </div>
      </header>

      <div>

        {/* ── HERO ── */}
        <section className="mx-auto px-10 pt-20 pb-14 flex flex-col items-center text-center">
          <h1 className="text-6xl font-extrabold text-black dark:text-white leading-tight mb-5">
            The Most Popular Use Cases
            <br />
            of{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
              Netraa APM
            </span>
          </h1>
          <p className="text-[15px] text-gray-500 dark:text-gray-400 leading-relaxed">
            Explore a wide range of powerful use cases that can be automated and streamlined with Netraa APM.
          </p>
        </section>

        {/* ── OVERVIEW ── */}
        <section className="mx-auto px-10 pb-16">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 dark:text-gray-500 px-1">Overview</span>
            <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
          </div>

          <div className="flex flex-col lg:flex-row gap-12 mb-10">
            <div className="lg:w-52 flex-shrink-0 flex flex-row gap-4">
              <div className="w-1 h-14 bg-orange-500 rounded-full" />
              <h2 className="text-[22px] font-extrabold text-gray-900 dark:text-gray-100 leading-snug"
                  >
                What is<br />
                <span className="text-orange-500">Netraa APM?</span>
              </h2>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-[1.85]">
                <span className="font-semibold text-gray-900 dark:text-white">Netraa APM</span> (Application Performance Monitoring) is a unified observability platform designed to monitor, analyze, and optimize the performance of modern applications and IT infrastructure in real time.
              </p>
              <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-[1.85]">
                It provides deep visibility across the entire application ecosystem by collecting and correlating multiple types of telemetry data, including metrics, logs, traces, and events. This enables teams to detect issues quickly, understand root causes, and ensure smooth application performance.
              </p>
              <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-[1.85]">
                Netraa APM supports both on-premises and cloud environments, making it suitable for complex, distributed architectures such as microservices-based applications.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Metrics", desc: "Real-time performance data", icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg> },
              { label: "Logs",    desc: "Centralized log management", icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
              { label: "Traces",  desc: "Distributed request tracing", icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg> },
              { label: "Events",  desc: "System & app event streams", icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> },
            ].map(({ label, desc, icon }) => (
              <div key={label}
                className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a] hover:border-orange-300 dark:hover:border-orange-800 transition-colors duration-200 group">
                <div className="w-8 h-8 rounded-lg bg-white dark:bg-[#222] border border-gray-100 dark:border-gray-700 flex items-center justify-center text-orange-500 flex-shrink-0 shadow-sm group-hover:border-orange-200 dark:group-hover:border-orange-800 transition-colors">
                  {icon}
                </div>
                <div>
                  <p className="text-[13px] font-bold text-gray-800 dark:text-gray-200 mb-0.5" style={{ fontFamily: "'Sora', sans-serif" }}>{label}</p>
                  <p className="text-[11.5px] text-gray-500 dark:text-gray-400 leading-snug">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── USE CASE CARDS ── */}
        <section className="mx-auto px-10 pb-20">
         <div className="mb-10">
  {/* Top divider + label */}
  <div className="flex items-center gap-4 mb-3">
    <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
    <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 dark:text-gray-500 px-1">
      Use Cases
    </span>
    <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
  </div>

  {/* Description */}
  <p className="text-[12px] text-gray-500 dark:text-gray-400 text-center max-w-lg mx-auto leading-relaxed">
    Each use case highlights a key capability of Netraa APM — covering real-world problems, how the platform addresses them, and the outcomes delivered. Click any card to explore more.
  </p>
</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {useCases.map((card, i) => (
              <div
                key={card.title}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => openModal(i)}
                className={`
                  rounded-2xl p-7 border cursor-default
                  bg-white dark:bg-[#1e1e1e]
                  transition-all duration-300 flex flex-col
                  ${hoveredCard === i
                    ? "border-orange-500 shadow-[0_8px_36px_rgba(249,115,22,0.15)] -translate-y-1"
                    : "border-gray-100 dark:border-[#2a2a2a]"
                  }
                `}
              >
                {/* Icon + badge row */}
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 dark:bg-[#2a1a0a] flex items-center justify-center text-orange-500 flex-shrink-0">
                    <span className="w-5 h-5 flex">{card.icon}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-orange-500 bg-orange-50 dark:bg-orange-500/10 px-2 py-1 rounded-full">
                    {card.subItems.length} sub-topics
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-[15px] font-bold text-gray-900 dark:text-gray-100 mb-2 leading-snug"
                    >
                  {card.title}
                </h3>

                {/* Description */}
                <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed mb-5 flex-1">
                  {card.desc}
                </p>

                {/* Sub-item previews */}
                {/* <ul className="space-y-1.5 mb-5">
                  {card.subItems.map((sub) => (
                    <li key={sub.num} className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
                      <span className="w-5 h-5 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-500 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                        {sub.num.split(".")[1]}
                      </span>
                      {sub.title}
                    </li>
                  ))}
                </ul> */}

                {/* Read more */}
                <button
                  onClick={() => openModal(i)}
                  className="text-[13px] font-semibold text-orange-500 hover:text-orange-600
                             inline-flex items-center gap-1 group transition-colors self-start"
                >
                  Read more
                  <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                </button>
              </div>
            ))}
          </div>
        </section>

 {/* ── REAL WORLD SCENARIOS ── */}
        <section className="relative mx-auto px-10 pb-2">
          <div className="absolute inset-0 -z-10 opacity-[0.03]
            bg-[radial-gradient(circle_at_1px_1px,#000_1px,transparent_0)]
            [background-size:20px_20px]" />
 
          <div className="flex flex-col items-center mb-16 text-center">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-orange-500 mb-3">Real World Scenarios</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white">
              How Netraa solves real production issues
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 max-w-xl">
              See how Netraa APM helps detect, diagnose, and resolve critical system failures in real environments.
            </p>
          </div>
 
          <div className="space-y-10">
            {[
              {
                title: "Payroll System Crash on Salary Day",
                desc: "Employees are unable to access the payroll system due to sudden slowdown and crashes.",
                without: ["Hard to identify issue", "Salary delays", "Employee dissatisfaction"],
                with: ["Detects API latency instantly", "Finds backend/database bottleneck", "Identifies root cause"],
                impact: ["Prevents payroll delays", "Saves HR time", "Improves employee trust"],
              },
              {
                title: "Banking App Transaction Failures",
                desc: "Users experience failed or delayed transactions during fund transfers.",
                without: ["Hard to trace failures", "Increased complaints"],
                with: ["Tracks transaction flow", "Identifies failing service", "Correlates backend issues"],
                impact: ["Faster resolution", "Reduced financial risk", "Better customer experience"],
              },
              {
                title: "High Memory Usage Causing Downtime",
                desc: "Application crashes due to excessive memory consumption.",
                without: ["Issue found after crash", "No root cause visibility"],
                with: ["Detects memory leaks early", "Alerts before failure"],
                impact: ["Prevents outages", "Reduces infra cost", "Improves stability"],
              },
              {
                title: "Database Lock Blocking Application",
                desc: "Application becomes unresponsive due to database locks.",
                without: ["Hard to detect locks", "Query pile-up"],
                with: ["Detects slow queries", "Maps DB to APIs"],
                impact: ["Faster recovery", "Reduced downtime", "Better DB performance"],
              },
            ].map((s) => (
              <div key={s.title}
                className="group rounded-2xl border border-gray-200 dark:border-[#2a2a2a]
                  bg-gray-50 dark:bg-[#1e1e1e]/80 p-8 transition-all duration-300
                  hover:shadow-[0_10px_50px_rgba(249,115,22,0.15)]">
                <h3 className="text-lg font-bold text-orange-500 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{s.desc}</p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white dark:bg-[#1e1e1e] border border-red-200/60 dark:border-red-900/40 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 dark:bg-red-500" />
                      <h4 className="font-semibold text-red-500 dark:text-red-400 text-sm">Without APM</h4>
                    </div>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">{s.without.map(x => <li key={x} className="flex items-start gap-1.5"><span className="text-red-400 mt-0.5">✕</span>{x}</li>)}</ul>
                  </div>
                  <div className="bg-white dark:bg-[#1e1e1e] border border-green-200/60 dark:border-green-900/40 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <h4 className="font-semibold text-green-600 dark:text-green-400 text-sm">With Netraa APM</h4>
                    </div>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">{s.with.map(x => <li key={x} className="flex items-start gap-1.5"><span className="text-green-500 mt-0.5">✓</span>{x}</li>)}</ul>
                  </div>
                  <div className="bg-white dark:bg-[#1e1e1e] border border-orange-200/60 dark:border-orange-900/40 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      <h4 className="font-semibold text-orange-500 dark:text-orange-400 text-sm">Business Impact</h4>
                    </div>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">{s.impact.map(x => <li key={x} className="flex items-start gap-1.5"><span className="text-orange-400 mt-0.5">↑</span>{x}</li>)}</ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
         {/* Footer */}
      <footer className="bg-white dark:bg-card-dark border-t border-border-color dark:border-white/10 py-10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Powered by</p>
            <div className="flex items-center gap-3 w-64">
      <div className="flex items-center justify-start -ml-4"> {/* Shift left */}
        <img
          src={logofooter}
          alt="Apmosys logo"
          className="h-10 w-auto dark:hidden"
        />
        <img
          src={logofooter}
          alt="Apmosys logo"
          className="h-10 w-auto hidden dark:block"
        />
      </div>
    </div>

          </div>
          <div className="text-sm text-text-muted text-center">
            © 2026 ApMoSys Products Private Ltd. All rights reserved.
          </div>
          <div className="flex items-center gap-6 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all">
            <div className="flex flex-col items-center gap-1 group cursor-pointer">
              <MdLockPerson className="text-3xl text-text-main dark:text-white" />
              <span className="text-[10px] font-bold text-text-main dark:text-white">SOC2 Type II</span>
            </div>
            <div className="w-px h-8 bg-border-color dark:bg-white/20"></div>
            <div className="flex flex-col items-center gap-1 group cursor-pointer">
              <MdVerified className="text-3xl text-text-main dark:text-white" />
              <span className="text-[10px] font-bold text-text-main dark:text-white">ISO 27001</span>
            </div>
          </div>
        </div>
      </footer>

      </div>

      {/* ── MODAL ── */}
      {modalData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Panel */}
          <div
            className="relative z-10 w-full max-w-2xl max-h-[85vh] overflow-y-auto hide-scrollbar
                        bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl
                        border border-gray-100 dark:border-[#2a2a2a] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={closeModal}
              className="absolute top-5 right-5 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                         hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            {/* Modal header */}
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-[#2a1a0a] flex items-center justify-center text-orange-500 flex-shrink-0">
                <span className="w-6 h-6 flex">{modalData.icon}</span>
              </div>
              <div>
                <h2 className="text-[18px] font-extrabold text-gray-900 dark:text-gray-100 leading-snug"
                    style={{ fontFamily: "'Sora', sans-serif" }}>
                  {modalData.title}
                </h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">{modalData.desc}</p>
              </div>
            </div>

            {/* Sub-items */}
            <div className="space-y-5">
              {modalData.subItems.map((sub, idx) => (
                <div key={sub.num}
                  className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#252525]">
                  {/* Number badge */}
                  <div className="w-8 h-8 rounded-full bg-orange-500 text-white text-[12px] font-bold
                                  flex items-center justify-center flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-gray-900 dark:text-gray-100 mb-1"
                       style={{ fontFamily: "'Sora', sans-serif" }}>
                      {sub.num} {sub.title}
                    </p>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                      {sub.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}